import React from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '../lib/utils';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

interface ModalProps {
  modalClassName?: string;
  children: React.ReactNode;
  modalMain?: string;
  size?: ModalSize;
  onClick?: () => void;
  closeButtonCLick?: () => void;
  hideCloseButton?: boolean;
  /**
   * Optional standard header: icon tile + title + one-line description,
   * separated from the body by a hairline. When `title` is set the body is
   * padded for you (`p-6`), so callers don't need their own wrapper.
   */
  title?: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  /** Optional standard footer (right-aligned actions) under a hairline. */
  footer?: React.ReactNode;
  /** Padding applied to the body when `title` is set. Default "p-6". */
  bodyClassName?: string;
}

const sizeClasses: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-[500px]',
  lg: 'max-w-3xl',
  xl: 'max-w-4xl',
};

const Modal = React.forwardRef<HTMLDivElement, ModalProps>(({
  modalClassName,
  modalMain,
  children,
  size = 'md',
  onClick,
  closeButtonCLick,
  hideCloseButton,
  title,
  description,
  icon,
  footer,
  bodyClassName,
  ...props
}, ref) => {
  // public/index.html provides #modal-root; test renderers (and any host page
  // without it) fall back to <body> so the modal still mounts and RTL's
  // `screen` queries can find it.
  const modalRoot = document.getElementById('modal-root') ?? document.body;
  const hasHeader = title !== undefined && title !== null;
  return ReactDOM.createPortal (
    <div
      onClick={onClick}
      className={cn('fixed inset-0 flex justify-center items-center bg-overlay/50 backdrop-blur-sm px-4 z-[999999]', modalMain)}
      {...props}
    >
      <div
        ref={ref}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        className={cn(
          'relative bg-surface border border-border shadow-xl min-w-[300px] w-full rounded-xl overflow-hidden transition-opacity duration-300 min-h-[200px] max-h-[85%] overflow-y-auto flex flex-col',
          sizeClasses[size],
          modalClassName,
        )}
      >
        {!hideCloseButton && (
          // Callers pass `closeButtonCLick={saving ? undefined : onClose}` to
          // lock the dialog mid-request — mirror that as a real disabled state
          // instead of an enabled-looking X that does nothing.
          <button
            onClick={closeButtonCLick}
            disabled={!closeButtonCLick}
            type='button'
            aria-label='Close'
            className='absolute top-4 right-4 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-surface-muted transition-colors z-10 disabled:opacity-40 disabled:pointer-events-none'
          >
            <X className="w-5 h-5" />
          </button>
        )}
        {hasHeader && (
          <div className="flex items-start gap-3 px-6 pt-5 pb-4 pr-12 border-b border-border shrink-0">
            {icon && (
              <div className="w-9 h-9 rounded-lg bg-primary-soft text-primary flex items-center justify-center shrink-0 [&>svg]:w-[18px] [&>svg]:h-[18px]">
                {icon}
              </div>
            )}
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-foreground leading-tight">{title}</h2>
              {description && <div className="text-xs text-muted-foreground mt-1">{description}</div>}
            </div>
          </div>
        )}
        {/* Header and footer are independent: the standard padded body wraps
            the children whenever either is present; legacy callers that pass
            neither keep rendering their children directly (they bring their
            own padding via modalClassName). */}
        {hasHeader || footer ? (
          <div className={cn('flex-1 min-h-0 overflow-y-auto p-6', bodyClassName)}>{children}</div>
        ) : (
          children
        )}
        {footer && (
          <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-border bg-surface-muted/40 shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>,
    modalRoot
  );
});

export default Modal;
